import { useEffect, useId, useRef, useState } from 'react'

/**
 * One collapsible section of the item tray (REC-65).
 *
 * THE HEADER IS THE SUMMARY. A collapsed section shows one line saying its
 * state ("Pre-order · every store except Paris · 1 Feb – 15 Mar · 1 each"),
 * so the collapsed tray reads as a spec sheet. A section that only said its
 * name would turn scrolling into clicking.
 *
 * MOUNTED ON FIRST OPEN, THEN KEPT. The body is not rendered until somebody
 * opens it — the audience adder loads every org unit on mount, and eight of
 * those at once for a glance at the price is waste — but once opened it stays
 * mounted and is only hidden, so collapsing a section does not throw away
 * what was typed in it. The unsaved dot then says so on the header.
 *
 * `open` is CONTROLLED by the editor, which takes its initial set from a prop:
 * the render suite opens sections without a click, because an assertion
 * against a shut panel passes against nothing (062).
 */
export function TraySection({
  title,
  summary,
  open,
  dirty = false,
  onToggle,
  children,
}: {
  title: string
  summary: string
  open: boolean
  dirty?: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  const [seen, setSeen] = useState(open)
  useEffect(() => {
    if (open) setSeen(true)
  }, [open])
  const bodyId = useId()

  return (
    <section className={`traysec${open ? ' is-open' : ''}`}>
      <h3 className="traysec__head">
        <button
          type="button"
          className="traysec__btn"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <span className="traysec__chev" aria-hidden="true" />
          <span className="traysec__title">
            {title}
            {dirty && (
              <span className="traysec__dot" title="Unsaved changes">
                <span className="sr-only"> (unsaved changes)</span>
              </span>
            )}
          </span>
          <span className="traysec__summary">{summary}</span>
        </button>
      </h3>
      {(open || seen) && (
        <div id={bodyId} className="traysec__body" hidden={!open}>
          {children}
        </div>
      )}
    </section>
  )
}

/**
 * Report a section's unsaved state upward, and clear it on unmount — a section
 * that remounts after a save (its key carries the server's values) must not
 * leave a stale dot behind.
 */
export function useReportDirty(dirty: boolean, onDirty?: (d: boolean) => void) {
  const latest = useRef(onDirty)
  latest.current = onDirty
  useEffect(() => {
    latest.current?.(dirty)
  }, [dirty])
  useEffect(() => () => latest.current?.(false), [])
}
